import { formatInvoiceSpecs } from '@/lib/invoiceSpecs';
import React, { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { fetchSaleById, fetchSalePayments } from '@/services/sales';
import { fetchShopSettings } from '@/services/settings';
import { queryKeys } from '@/lib/queryKeys';
import { parseDatabaseError } from '@/lib/errors';
import { Sale, ShopSettings } from '@/types/models';
import { ErrorAlert } from '@/components/common/ErrorAlert';
import { SalePaymentModal } from './SalePaymentModal';
import { SaleCorrectionModal } from './SaleCorrectionModal';
import { ShareInvoiceModal } from './ShareInvoiceModal';
import { CreateReturnForm } from '@/features/returns/CreateReturnForm';
import { useAuth } from '@/context/AuthContext';
import { printInvoiceDocument } from '@/lib/invoicePrinter';
import {
  DollarSign,
  Printer,
  CreditCard,
  Loader2,
  Edit3,
  RotateCcw,
  Share2,
} from 'lucide-react';

interface SaleDetailProps {
  saleId: string;
  onClose: () => void;
}

function formatDate(iso: string): string {
  return new Date(iso).toLocaleDateString('en-PK', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
}

function formatCurrency(val: number | null | undefined): string {
  if (val == null) return '—';
  return `Rs. ${val.toLocaleString('en-PK')}`;
}

export const SaleDetail: React.FC<SaleDetailProps> = ({ saleId, onClose }) => {
  const { role, hasPermission } = useAuth();
  const isOwner = role === 'owner';
  const canViewCosts = hasPermission('purchases');
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [showCorrectionModal, setShowCorrectionModal] = useState(false);
  const [showReturnModal, setShowReturnModal] = useState(false);
  const [showShareModal, setShowShareModal] = useState(false);

  const { data: sale, isLoading, error, refetch } = useQuery<Sale>({
    queryKey: queryKeys.sales.detail(saleId),
    queryFn: () => fetchSaleById(saleId),
  });

  const { data: payments = [] } = useQuery({
    queryKey: queryKeys.sales.payments(saleId),
    queryFn: () => fetchSalePayments(saleId),
    enabled: !!sale,
  });

  const { data: shopSettings } = useQuery<ShopSettings>({
    queryKey: queryKeys.settings.shop,
    queryFn: () => fetchShopSettings(),
  });

  const balanceDue = sale ? Math.max(0, sale.balance_due ?? (sale.total - sale.amount_paid)) : 0;

  // Calculate profit for owner/manager (Net revenue excludes sales tax liability)
  const totalCost = sale?.items?.reduce((acc, item) => acc + (item.line_cost || 0), 0) ?? 0;
  const netRevenue = sale ? (sale.subtotal - (sale.discount || 0)) : 0;
  const totalProfit = netRevenue - totalCost;

  const handlePrintInvoice = () => {
    if (!sale) return;
    printInvoiceDocument({
      shop: {
        shop_name: shopSettings?.shop_name || 'AL KUWAIT TECH',
        slogan: shopSettings?.slogan || 'ALWAYS AHEAD.',
        address: shopSettings?.address || 'Shop 264, 2nd Floor Umair Center, 6th Road, Rawalpindi',
        phone: shopSettings?.phone || '042 111 222 345',
        phone_secondary: shopSettings?.phone_secondary || '+92 370 5478785',
        phone_tertiary: shopSettings?.phone_tertiary || '+92 324 9554169',
        email: shopSettings?.email || null,
        website: shopSettings?.website || null,
        invoice_footer:
          shopSettings?.invoice_footer ||
          'Thank you for choosing Al Kuwait Tech. We appreciate your business and look forward to serving you again.',
      },
      sale,
    });
  };

  const phones: string[] = [];
  if (shopSettings?.phone) phones.push(shopSettings.phone);
  if (shopSettings?.phone_secondary) phones.push(shopSettings.phone_secondary);
  if (shopSettings?.phone_tertiary) phones.push(shopSettings.phone_tertiary);
  if (phones.length === 0) {
    phones.push('042 111 222 345', '+92 370 5478785', '+92 324 9554169');
  }

  return (
    <>
      {isLoading && (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="w-8 h-8 text-brand-green animate-spin" />
        </div>
      )}

      {error && <ErrorAlert message={parseDatabaseError(error)} onRetry={() => refetch()} />}

      {sale && !isLoading && (
        <div className="space-y-4 max-h-[75vh] overflow-y-auto pr-1 text-xs">
          {/* Action Bar */}
          <div className="flex items-center justify-between p-3 bg-brand-bg rounded-lg border border-brand-border">
            <div className="flex items-center space-x-2">
              <span className="font-bold text-sm text-brand-black">Invoice #{sale.invoice_number}</span>
              <span
                className={`px-2 py-0.5 text-[10px] font-bold rounded-full uppercase ${
                  sale.payment_status === 'paid'
                    ? 'bg-green-100 text-green-800'
                    : sale.payment_status === 'partial'
                    ? 'bg-amber-100 text-amber-800'
                    : 'bg-red-100 text-red-800'
                }`}
              >
                {sale.payment_status}
              </span>
            </div>

            <div className="flex items-center space-x-2 flex-wrap gap-y-1">
              <button
                type="button"
                onClick={handlePrintInvoice}
                className="inline-flex items-center space-x-1.5 px-3 py-1.5 bg-white text-brand-black font-bold rounded border border-brand-border hover:bg-slate-50 transition-colors shadow-xs"
              >
                <Printer className="w-4 h-4 text-brand-green" />
                <span>Print Invoice (A5)</span>
              </button>
              <button
                type="button"
                onClick={() => setShowShareModal(true)}
                className="inline-flex items-center space-x-1.5 px-3 py-1.5 bg-[#25D366] text-white font-bold rounded hover:bg-[#1EBE5D] transition-colors shadow-xs"
                title="Share verified digital invoice link via WhatsApp or Email"
              >
                <Share2 className="w-4 h-4" />
                <span>Share Invoice</span>
              </button>
              {isOwner && (
                <button
                  type="button"
                  onClick={() => setShowCorrectionModal(true)}
                  className="inline-flex items-center space-x-1.5 px-3 py-1.5 bg-amber-50 text-amber-900 border border-amber-300 font-bold rounded hover:bg-amber-100 transition-colors shadow-xs"
                  title="Edit or correct invoice metadata, prices or terms (Owner only)"
                >
                  <Edit3 className="w-4 h-4 text-amber-600" />
                  <span>Edit / Correct Invoice</span>
                </button>
              )}
              {canViewCosts && sale.status !== 'cancelled' && (
                <button
                  type="button"
                  onClick={() => setShowReturnModal(true)}
                  className="inline-flex items-center space-x-1.5 px-3 py-1.5 bg-white text-brand-black font-bold rounded border border-brand-border hover:bg-slate-50 transition-colors shadow-xs"
                >
                  <RotateCcw className="w-4 h-4 text-brand-red" />
                  <span>Return / Refund</span>
                </button>
              )}
              {balanceDue > 0 && (
                <button
                  type="button"
                  onClick={() => setShowPaymentModal(true)}
                  className="inline-flex items-center space-x-1 px-3 py-1.5 bg-brand-green text-white font-bold rounded hover:bg-brand-green-hover transition-colors shadow-sm"
                >
                  <DollarSign className="w-4 h-4" />
                  <span>Record Payment</span>
                </button>
              )}
            </div>
          </div>

          {/* On-screen A5 Branded Invoice Card */}
          <div className="bg-white p-5 rounded-xl border border-brand-border shadow-xs space-y-4 max-w-3xl mx-auto">
            {/* Header: Logo, Brand Info & Invoice Meta */}
            <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center pb-3 border-b-2 border-brand-green gap-3">
              <div className="flex items-center space-x-3">
                <img
                  src="/reference/al-kuwait-tech-logo.png"
                  alt="Al Kuwait Tech Logo"
                  className="w-12 h-12 object-contain shrink-0"
                  onError={(e) => {
                    (e.target as HTMLElement).style.display = 'none';
                  }}
                />
                <div>
                  <h2 className="text-lg font-black tracking-tight text-brand-black">
                    {shopSettings?.shop_name || 'AL KUWAIT TECH'}
                  </h2>
                  <p className="text-[10px] font-extrabold text-brand-red tracking-widest uppercase">
                    {shopSettings?.slogan || 'ALWAYS AHEAD.'}
                  </p>
                  <p className="text-[11px] text-brand-muted mt-0.5">
                    {shopSettings?.address || 'Shop 264, 2nd Floor Umair Center, 6th Road, Rawalpindi'}
                  </p>
                  <p className="text-[10px] text-brand-black font-semibold mt-0.5">
                    Phone: {phones.join(' • ')}
                  </p>
                </div>
              </div>

              <div className="text-left sm:text-right">
                <p className="text-xs font-black text-brand-green uppercase tracking-wider">SALES INVOICE</p>
                <p className="text-base font-bold font-mono text-brand-black">#INV-{sale.invoice_number}</p>
                <p className="text-[11px] text-brand-muted">Date: <strong className="text-brand-black">{formatDate(sale.sale_date)}</strong></p>
              </div>
            </div>

            {/* Customer Information Row */}
            <div className="bg-brand-bg p-3 rounded-lg border border-brand-border grid grid-cols-1 sm:grid-cols-2 gap-2 text-xs">
              <div>
                <p className="text-[10px] font-bold text-brand-muted uppercase tracking-wider mb-0.5">Billed To:</p>
                {sale.customer_name_snapshot || sale.customer?.name ? (
                  <div>
                    <p className="font-bold text-brand-black">{sale.customer_name_snapshot || sale.customer?.name}</p>
                    {(sale.customer_phone_snapshot || sale.customer?.phone) && (
                      <p className="text-brand-muted">{sale.customer_phone_snapshot || sale.customer?.phone}</p>
                    )}
                    {(sale.customer_email_snapshot || sale.customer?.email) && (
                      <p className="text-brand-muted text-[11px]">{sale.customer_email_snapshot || sale.customer?.email}</p>
                    )}
                    {(sale.customer_address_snapshot || sale.customer?.address) && (
                      <p className="text-brand-muted text-[11px]">{sale.customer_address_snapshot || sale.customer?.address}</p>
                    )}
                  </div>
                ) : (
                  <p className="text-brand-muted italic">Walk-in Customer / Cash Sale</p>
                )}
              </div>
              <div className="sm:text-right">
                <p className="text-[10px] font-bold text-brand-muted uppercase tracking-wider mb-0.5">Payment Status:</p>
                <p className="font-bold text-brand-black capitalize">{sale.payment_status}</p>
                <p className="text-[11px] text-brand-muted">Issued on {formatDate(sale.sale_date)}</p>
              </div>
            </div>

            {/* Line Items Table */}
            <div>
              <div className="border border-brand-border rounded-lg overflow-hidden">
                <table className="w-full text-xs">
                  <thead className="bg-brand-bg border-b border-brand-border text-brand-muted font-bold uppercase tracking-wider text-[10px]">
                    <tr>
                      <th className="text-left px-3 py-2">#</th>
                      <th className="text-left px-3 py-2">Item / Sold Specs</th>
                      <th className="text-center px-3 py-2">Qty</th>
                      <th className="text-right px-3 py-2">Unit Price</th>
                      <th className="text-right px-3 py-2">Discount</th>
                      <th className="text-right px-3 py-2">Line Total</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-brand-border">
                    {sale.items?.map((item, idx) => {
                      const specDetails = formatInvoiceSpecs(item);

                      return (
                        <tr key={item.id} className="hover:bg-brand-bg/50">
                          <td className="px-3 py-2 text-brand-muted">{idx + 1}</td>
                          <td className="px-3 py-2">
                            <p className="font-bold text-brand-black">{item.item_name}</p>
                            {specDetails && (
                              <p className="text-[11px] font-semibold text-brand-green">{specDetails}</p>
                            )}
                            {(item.serial_snapshot || item.sku_snapshot) && (
                              <p className="text-[10px] text-brand-muted font-mono">
                                {item.serial_snapshot ? `S/N: ${item.serial_snapshot}` : `SKU: ${item.sku_snapshot}`}
                              </p>
                            )}
                          </td>
                          <td className="px-3 py-2 text-center font-semibold">{item.quantity}</td>
                          <td className="px-3 py-2 text-right font-medium">{formatCurrency(item.unit_price)}</td>
                          <td className="px-3 py-2 text-right text-brand-red font-medium">
                            {item.line_discount > 0 ? `- ${formatCurrency(item.line_discount)}` : '—'}
                          </td>
                          <td className="px-3 py-2 text-right font-bold text-brand-black">
                            {formatCurrency(item.line_total)}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>

            {/* Totals & Financial Summary */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="space-y-2">
                {sale.notes && (
                  <div className="p-3 bg-brand-bg print:bg-gray-50 rounded border border-brand-border print:border-black">
                    <p className="font-bold text-brand-black">Terms / Notes</p>
                    <p className="text-brand-muted print:text-black">{sale.notes}</p>
                  </div>
                )}

                {canViewCosts && (
                  <div className="p-3 bg-green-50/60 rounded border border-green-200 text-green-900 print:hidden">
                    <p className="font-bold uppercase tracking-wider text-[10px]">Owner Profit Breakdown</p>
                    <div className="flex justify-between mt-1 font-semibold">
                      <span>Total COGS: {formatCurrency(totalCost)}</span>
                      <span>Net Profit: {formatCurrency(totalProfit)}</span>
                    </div>
                  </div>
                )}
              </div>

              <div className="bg-brand-bg print:bg-gray-50 p-3 rounded-lg border border-brand-border print:border-black space-y-1.5">
                <div className="flex justify-between font-semibold">
                  <span>Subtotal:</span>
                  <span>{formatCurrency(sale.subtotal)}</span>
                </div>
                {sale.discount > 0 && (
                  <div className="flex justify-between text-brand-red">
                    <span>Overall Discount:</span>
                    <span>- {formatCurrency(sale.discount)}</span>
                  </div>
                )}
                {sale.tax > 0 && (
                  <div className="flex justify-between">
                    <span>Tax:</span>
                    <span>+ {formatCurrency(sale.tax)}</span>
                  </div>
                )}
                <div className="flex justify-between text-sm font-bold text-brand-black border-t pt-1 border-brand-border print:border-black">
                  <span>Grand Total:</span>
                  <span className="text-brand-green">{formatCurrency(sale.total)}</span>
                </div>
                <div className="flex justify-between text-brand-muted print:text-black">
                  <span>Amount Paid:</span>
                  <span className="text-brand-green font-semibold">{formatCurrency(sale.amount_paid)}</span>
                </div>
                <div className="flex justify-between text-sm font-bold border-t pt-1 border-brand-border print:border-black">
                  <span>Balance Due:</span>
                  <span className={balanceDue > 0 ? 'text-brand-red' : 'text-brand-green'}>
                    {formatCurrency(balanceDue)}
                  </span>
                </div>
              </div>
            </div>

            {/* Payment History Section */}
            {payments.length > 0 && (
              <div className="pt-2 border-t border-brand-border print:border-black">
                <div className="flex items-center justify-between mb-2">
                  <h4 className="font-bold text-brand-black uppercase tracking-wider flex items-center space-x-1">
                    <CreditCard className="w-3.5 h-3.5 text-brand-green print:hidden" />
                    <span>Payment History ({payments.length})</span>
                  </h4>
                  <div className="text-[11px] text-brand-muted space-x-3 print:text-black font-mono">
                    <span>Received: <strong className="text-brand-green">{formatCurrency(payments.filter(p => p.direction === 'in').reduce((s, p) => s + (p.amount || 0), 0))}</strong></span>
                    {payments.some(p => p.direction === 'out') && (
                      <span>Refunded: <strong className="text-brand-red">{formatCurrency(payments.filter(p => p.direction === 'out').reduce((s, p) => s + (p.amount || 0), 0))}</strong></span>
                    )}
                  </div>
                </div>
                <div className="space-y-1.5">
                  {payments.map((p) => {
                    const isRefund = p.direction === 'out';
                    return (
                      <div
                        key={p.id}
                        className={`flex items-center justify-between p-2 bg-white print:bg-gray-50 rounded border ${isRefund ? 'border-amber-300 bg-amber-50/40' : 'border-brand-border'} print:border-black`}
                      >
                        <div>
                          <div className="flex items-center space-x-1.5">
                            <span className={`font-semibold ${isRefund ? 'text-brand-red font-bold' : 'text-brand-green'}`}>
                              {isRefund ? '-' : ''}{formatCurrency(p.amount)}
                            </span>
                            <span className={`px-1.5 py-0.2 text-[9px] font-bold rounded uppercase ${isRefund ? 'bg-amber-100 text-amber-900 border border-amber-300' : 'bg-slate-100 text-slate-700'}`}>
                              {isRefund ? 'Refund' : 'Payment'}
                            </span>
                            <span className="text-brand-muted print:text-black"> via {p.payment_method}</span>
                          </div>
                          {p.reference && <span className="text-brand-muted print:text-black text-[10px]"> (Ref: {p.reference})</span>}
                          {p.notes && <p className="text-[10px] text-brand-muted print:text-black">{p.notes}</p>}
                        </div>
                        <span className="text-brand-muted print:text-black text-xs">{formatDate(p.payment_date || p.created_at)}</span>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Invoice Footer / Closing */}
            <div className="border-t border-brand-border pt-3 text-center text-xs text-brand-muted space-y-0.5">
              <p>{shopSettings?.invoice_footer || 'Thank you for choosing Al Kuwait Tech. We appreciate your business and look forward to serving you again.'}</p>
              <p className="font-extrabold text-brand-green tracking-wider text-[11px]">{shopSettings?.slogan || 'ALWAYS AHEAD.'}</p>
            </div>
          </div>

          {showPaymentModal && (
            <SalePaymentModal sale={sale} isOpen={showPaymentModal} onClose={() => setShowPaymentModal(false)} />
          )}

          {showCorrectionModal && (
            <SaleCorrectionModal
              sale={sale}
              isOpen={showCorrectionModal}
              onClose={() => {
                setShowCorrectionModal(false);
                void refetch();
              }}
            />
          )}

          {showReturnModal && (
            <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4">
              <div className="bg-white rounded-xl shadow-xl w-full max-w-3xl max-h-[90vh] overflow-y-auto p-5">
                <div className="flex items-center justify-between pb-3 mb-4 border-b border-brand-border">
                  <h3 className="font-bold text-base text-brand-black">Process Return for Invoice #{sale.invoice_number}</h3>
                  <button
                    type="button"
                    onClick={() => setShowReturnModal(false)}
                    className="text-brand-muted hover:text-brand-black text-sm font-semibold"
                  >
                    Cancel / Close
                  </button>
                </div>
                <CreateReturnForm
                  initialSale={sale}
                  onSuccess={() => {
                    setShowReturnModal(false);
                    void refetch();
                  }}
                  onCancel={() => setShowReturnModal(false)}
                />
              </div>
            </div>
          )}

          {showShareModal && (
            <ShareInvoiceModal
              sale={sale}
              isOpen={showShareModal}
              onClose={() => setShowShareModal(false)}
            />
          )}
        </div>
      )}

      {/* Footer */}
      <div className="flex justify-end pt-3 border-t border-brand-border print:hidden">
        <button onClick={onClose} className="px-4 py-2 text-sm font-medium text-brand-muted hover:text-brand-black">
          Close
        </button>
      </div>
    </>
  );
};
